import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy, AlertCircle, RefreshCw } from "lucide-react";
import type { Business } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import {
  PeriodSelector,
  LeaderboardTable,
  AnalyticsUpgrade,
  ExportButton,
  type AnalyticsPeriod,
} from "@/components/analytics";

interface StaffLeaderboardEntry {
  staffId: string;
  staffName: string;
  staffSlug: string;
  reviewCount: number;
  avgRating: number;
  requestsSent: number;
  responseRate: number;
}

export default function Leaderboard() {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");

  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const isFreeUser = business?.planType === "free";

  const { data: leaderboard, isLoading, isError, refetch } = useQuery<StaffLeaderboardEntry[]>({
    queryKey: [`/api/staff/leaderboard?period=${period}`],
    enabled: !isFreeUser, // Only fetch if not a free user
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold flex items-center gap-3">
              <Trophy className="h-8 w-8 text-yellow-500" />
              Staff Leaderboard
            </h1>
            <p className="text-muted-foreground mt-1">
              See which team members are bringing in the most reviews
            </p>
          </div>
        </div>

        {/* Period Selector and Export - Show for all users */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {!isFreeUser && <PeriodSelector value={period} onChange={setPeriod} />}
          <ExportButton type="leaderboard" period={period} isFreeUser={isFreeUser} />
        </div>
      </div>

      {/* Leaderboard Content */}
      {isFreeUser ? (
        <AnalyticsUpgrade variant="full" />
      ) : isError ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Leaderboard</h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                We couldn't load the leaderboard data. This might be a temporary network issue.
              </p>
              <Button onClick={() => refetch()} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <LeaderboardTable
          leaderboard={leaderboard || []}
          businessSlug={business?.slug}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
